import React, { useState } from 'react';
import RegisterPage from './RegisterPage';
import { logCalculatorClick } from './supabaseClient'; // Adjust path if needed

const TDM_DRUGS = [
  { id: 'vanco', name: 'Vancomycin', class: 'Glycopeptide Antibiotic', kinetics: 'AUC/MIC & Trough Optimization' },
  { id: 'amino', name: 'Aminoglycoside', class: 'Aminoglycoside Antibiotic', kinetics: 'Hartford Nomogram / Traditional' },
  { id: 'valproic', name: 'Valproic Acid', class: 'Anticonvulsant', kinetics: 'Protein Binding Dependent' },
  { id: 'pheny', name: 'Phenytoin', class: 'Anticonvulsant', kinetics: 'Non-linear Saturation' },
  { id: 'carbamazepine', name: 'Carbamazepine', class: 'Anticonvulsant', kinetics: 'Auto-Induction Timelines' },
  { id: 'phenobarbitone', name: 'Phenobarbitone', class: 'Anticonvulsant / Sedative', kinetics: 'Long Half-Life Linear' },
  { id: 'lithium', name: 'Lithium', class: 'Mood Stabilizer', kinetics: 'Renal Clearance Scaling' },
  { id: 'cyclosporin', name: 'Cyclosporin', class: 'Immunosuppressant', kinetics: 'Indication Matrix Target Tracking' },
  { id: 'theophylline', name: 'Theophylline', class: 'Bronchodilator', kinetics: 'Multi-Factor Clearance' },
  { id: 'tacrolimus', name: 'Tacrolimus', class: 'Immunosuppressant', kinetics: 'Smart Weight Engine & Safety Guard' },
  { id: 'digoxin', name: 'Digoxin', class: 'Inotropic Effect ', kinetics: 'Dose Initiation & Interaction Telemetry Matrix' },
  { id: 'salicylate', name: 'Salicylate', class: 'Analgesic / NSAID', kinetics: 'Serial Concentration & Treatment Guidance Worksheet' },
  { id: 'sirolimus', name: 'Sirolimus', class: 'Immunosuppressant', kinetics: 'Precision Kinetic Range & Stratification Workspace' },
  { id: 'methotrexate', name: 'Methotrexate', class: 'Immunosuppressant', kinetics: 'High-Dose Elimination Tracking & Antidote Optimization Dashboard' },
  { id: 'paracetamol', name: 'Paracetamol', class: 'Analgesic / Antipyretic', kinetics: 'BSA & Weight-Calibrated Antidote Resuscitation Panel' },
];

function DashboardLaunchpad({ onSelectDrug, user, setUser }) {
  const [search, setSearchTerm] = useState('');
  const [showRegisterGate, setShowRegisterGate] = useState(false);
  const [pendingDrugSelection, setPendingDrugSelection] = useState(null);

  const handleDrugClick = (drugId) => {
    // Always grant free access to baseline anti-infectives
    if (drugId === 'vanco' || drugId === 'amino') {
      logCalculatorClick(drugId, user ? user.email : null);
      onSelectDrug(drugId);
      return;
    }

    // Secure Premium Interception Check
    if (user) {
      logCalculatorClick(drugId, user.email); // 👈 Passes the true logged-in pharmacist email!
      onSelectDrug(drugId);
    } else {
      setPendingDrugSelection(drugId);
      setShowRegisterGate(true);
    }
  };

  const handleAuthSuccess = (authenticatedUser) => {
    setUser(authenticatedUser);
    setShowRegisterGate(false);
    if (pendingDrugSelection) {
      logCalculatorClick(pendingDrugSelection);
      onSelectDrug(pendingDrugSelection);
      setPendingDrugSelection(null);
    }
  };

  if (showRegisterGate) {
    return (
      <RegisterPage 
        onAuthSuccess={handleAuthSuccess} 
        onBackToDashboard={() => {
          setShowRegisterGate(false);
          setPendingDrugSelection(null);
        }} 
      />
    );
  }

  const filtered = TDM_DRUGS.filter(d => 
    d.name.toLowerCase().includes(search.toLowerCase()) || 
    d.class.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div style={{ fontSize: '0.8rem', color: user ? '#2ecc71' : '#3498db', marginBottom: '15px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        Scope: {user ? `Authenticated Ward Access (${user.email}) ✅` : '🔓 Open-Access Standard Mode'}
      </div>
      
      <div style={{ marginBottom: '25px' }}>
        <input 
          type="text" 
          placeholder="🔍 Search drug name or therapeutic class (e.g., Immunosuppressant)..."
          value={search}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ width: '100%', padding: '14px 20px', background: '#2c3e50', border: '2px solid #34495e', borderRadius: '8px', color: '#fff', fontSize: '1rem', outline: 'none' }}
        />
      </div>

      <header style={{ textAlign: 'center', marginBottom: '25px' }}>
        <h1 style={{ color: '#F8FAFC', margin: '0 0 5px 0' }}>🏥 Clinical TDM Assistant</h1>
        <p style={{ color: '#94A3B8', fontSize: '1rem', margin: 0 }}>Handbook-Aligned Dosing Frameworks.</p>
      </header>
      
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        {filtered.map(drug => {
          const isPremium = drug.id !== 'vanco' && drug.id !== 'amino';
          return (
            <div 
              key={drug.id}
              onClick={() => handleDrugClick(drug.id)}
              style={{ background: '#1e272e', border: '1px solid #3d4e5d', borderRadius: '10px', padding: '20px', cursor: 'pointer', position: 'relative' }}
            >
              {isPremium && !user && (
                <span style={{ position: 'absolute', top: '15px', right: '15px', fontSize: '0.62rem', fontWeight: 'bold', color: '#d6a2e8', background: 'rgba(155, 89, 182, 0.15)', padding: '3px 8px', borderRadius: '12px', border: '1px solid rgba(155, 89, 182, 0.3)' }}>
                  🔑 PREMIUM LOCK
                </span>
              )}
              <h4 style={{ margin: '0 0 5px 0', color: '#3498db', fontSize: '1.25rem' }}>{drug.name}</h4>
              <span style={{ fontSize: '0.75rem', color: '#95a5a6', textTransform: 'uppercase', fontWeight: 'bold', display: 'block', marginBottom: '10px' }}>{drug.class}</span>
              <div style={{ fontSize: '0.8rem', color: '#bdc3c7', background: 'rgba(0,0,0,0.2)', padding: '6px 10px', borderRadius: '4px' }}>
                <strong style={{ color: '#f1c40f' }}>Kinetics:</strong> {drug.kinetics}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default DashboardLaunchpad;